import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'react-tooltip';
import { Icon } from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import './tooltipTrigger.scss';

const useClickOutside = (refs, handler, { eventType = 'pointerdown', capture = true } = {}) => {
  useEffect(() => {
    const listener = (evt) => {
      const path = evt.composedPath?.() || [];
      const inside = refs.some((r) => r.current && (path.includes(r.current) || r.current.contains(evt.target)));
      if (!inside) handler(evt);
    };
    window.addEventListener(eventType, listener, { capture });
    return () => window.removeEventListener(eventType, listener, { capture });
  }, [refs, handler, eventType, capture]);
};

export const TooltipTrigger = ({
  id,
  trigger,
  content,
  placement = 'top',
  mode = 'click',
  tooltipClass = 'ps-tooltip',
  tooltipBg = '#fff',
  tooltipColor = '#222',
  tooltipMaxWidth = 160,
  underline = false,
  href,
  className = '',
  hideDelay = 80,
  ...rest
}) => {
  const tooltipStyle = {
    background: tooltipBg,
    color: tooltipColor,
    maxWidth: tooltipMaxWidth
  };

  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const tipRef = useRef(null);

  useClickOutside([anchorRef, tipRef], () => setOpen(false));

  const timer = useRef(null);
  const clearTimer = () => {
    // eslint-disable-next-line no-unused-expressions
    timer.current && clearTimeout(timer.current);
  };
  const scheduleClose = () => {
    clearTimer();
    timer.current = setTimeout(() => setOpen(false), hideDelay);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      setOpen((prev) => !prev);
    }
  };

  useEffect(() => {
    if (mode === 'hover') {
      if (!open) return;
      const el = document.getElementById(id);
      if (!el) return;
      tipRef.current = el;
      el.addEventListener('mouseenter', clearTimer);
      el.addEventListener('mouseleave', scheduleClose);
      // eslint-disable-next-line consistent-return
      return () => {
        el.removeEventListener('mouseenter', clearTimer);
        el.removeEventListener('mouseleave', scheduleClose);
        tipRef.current = null;
      };
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, id, hideDelay, mode]);

  const isHover = mode === 'hover';
  const isClick = mode === 'click';

  const anchorCls = [className, underline && 'ps-underline'].filter(Boolean).join(' ');

  return (
    <span className="tooltip-trigger-wrapper">
      <a
        ref={anchorRef}
        data-tooltip-id={id}
        href={href}
        role={href ? undefined : 'button'}
        className={anchorCls}
        style={underline ? { textDecoration: 'underline' } : undefined}
        tabIndex={href ? undefined : 0}
        onMouseEnter={isHover ? () => setOpen(true) : undefined}
        onMouseLeave={isHover ? scheduleClose : undefined}
        onFocus={isHover ? () => setOpen(true) : undefined}
        onBlur={isHover ? () => setOpen(false) : undefined}
        onClick={isClick ? () => setOpen((v) => !v) : undefined}
        onKeyDown={handleKeyDown}
        aria-label={href ? undefined : `Show tooltip: ${trigger} : 'More information'}`}
        {...rest}
      >
        {trigger}
      </a>

      <Tooltip
        id={id}
        isOpen={open}
        setIsOpen={setOpen}
        openEvents={{
          mouseover: isHover,
          focus: isHover,
          click: isClick
        }}
        closeEvents={{
          mouseout: isHover,
          blur: isHover,
          click: isClick
        }}
        globalCloseEvents={{ clickOutsideAnchor: true }}
        clickable
        closeOnClick={false}
        place={placement}
        className={tooltipClass}
        style={tooltipStyle}
      >
        <div className="tooltip-content-wrapper">
          <button
            type="button"
            className="tooltip-close-button"
            onClick={() => setOpen(false)}
            aria-label="Close tooltip"
          >
            <Icon icon="close" />
          </button>
          <div className="tooltip-content">{content}</div>
        </div>
      </Tooltip>
    </span>
  );
};

TooltipTrigger.propTypes = {
  id: PropTypes.string.isRequired,
  trigger: PropTypes.node.isRequired,
  content: PropTypes.node.isRequired,
  placement: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  mode: PropTypes.oneOf(['hover', 'click']),
  tooltipClass: PropTypes.string,
  underline: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  href: PropTypes.string,
  className: PropTypes.string,
  tooltipBg: PropTypes.string,
  tooltipColor: PropTypes.string,
  tooltipMaxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  hideDelay: PropTypes.number
};
